import { GeneratedType, Registry } from '@cosmjs/proto-signing'
import { defaultRegistryTypes } from '@cosmjs/stargate'

import {
  MsgAddTokenInfo,
  MsgAddTxToStopList,
  MsgDeleteChain,
  MsgDeleteToken,
  MsgInsertChain,
  MsgInsertToken,
  MsgRemoveReferral,
  MsgRemoveTokenInfo,
  MsgRemoveTransaction,
  MsgRemoveTxFromStopList,
  MsgSetReferral,
  MsgStartEpoch,
  MsgUpdateToken,
} from '@/generated/proto/bridge/tx'

import {
  CHAIN_ACTION_DESCRIPTIONS,
  CHAIN_ACTION_LABELS,
} from './constructor/modules/chain/constants'
import {
  EPOCH_ACTION_DESCRIPTIONS,
  EPOCH_ACTION_LABELS,
} from './constructor/modules/epoch/constants'
import {
  REFERRAL_ACTION_DESCRIPTIONS,
  REFERRAL_ACTION_LABELS,
} from './constructor/modules/referral/constants'
import {
  STOPLIST_ACTION_DESCRIPTIONS,
  STOPLIST_ACTION_LABELS,
} from './constructor/modules/stoplist/constants'
import {
  TOKEN_ACTION_DESCRIPTIONS,
  TOKEN_ACTION_LABELS,
} from './constructor/modules/token/constants'
import {
  TOKEN_INFO_ACTION_DESCRIPTIONS,
  TOKEN_INFO_ACTION_LABELS,
} from './constructor/modules/tokeninfo/constants'
import {
  TRANSACTION_ACTION_DESCRIPTIONS,
  TRANSACTION_ACTION_LABELS,
} from './constructor/modules/transaction/constants'
import {
  TRANSFER_ACTION_DESCRIPTIONS,
  TRANSFER_ACTION_LABELS,
} from './constructor/modules/transfer/constants'

interface ModuleConfig {
  label: string
  overview: string
  actions: Record<string, string>
  descriptions: Record<string, string>
}

export const MODULE_REGISTRY = {
  transfer: {
    label: 'Transfer',
    overview: 'Send tokens from one address to another',
    actions: TRANSFER_ACTION_LABELS,
    descriptions: TRANSFER_ACTION_DESCRIPTIONS,
  },
  chain: {
    label: 'Chain',
    overview: 'Insert or delete chain configuration',
    actions: CHAIN_ACTION_LABELS,
    descriptions: CHAIN_ACTION_DESCRIPTIONS,
  },
  token: {
    label: 'Token',
    overview: 'Insert, update, or delete token configuration',
    actions: TOKEN_ACTION_LABELS,
    descriptions: TOKEN_ACTION_DESCRIPTIONS,
  },
  tokeninfo: {
    label: 'Token Info',
    overview: 'Add or remove token information for specific chains',
    actions: TOKEN_INFO_ACTION_LABELS,
    descriptions: TOKEN_INFO_ACTION_DESCRIPTIONS,
  },
  referral: {
    label: 'Referral',
    overview: 'Manage referral configurations and rewards',
    actions: REFERRAL_ACTION_LABELS,
    descriptions: REFERRAL_ACTION_DESCRIPTIONS,
  },
  transaction: {
    label: 'Transaction',
    overview: 'Remove transactions from the system',
    actions: TRANSACTION_ACTION_LABELS,
    descriptions: TRANSACTION_ACTION_DESCRIPTIONS,
  },
  stoplist: {
    label: 'Stop List',
    overview: 'Manage transaction stop list',
    actions: STOPLIST_ACTION_LABELS,
    descriptions: STOPLIST_ACTION_DESCRIPTIONS,
  },
  epoch: {
    label: 'Epoch',
    overview: 'Manage epochs and TSS configuration',
    actions: EPOCH_ACTION_LABELS,
    descriptions: EPOCH_ACTION_DESCRIPTIONS,
  },
} as const satisfies Record<string, ModuleConfig>

export type SupportedModule = keyof typeof MODULE_REGISTRY

export const moduleLabelsMap = Object.fromEntries(
  Object.entries(MODULE_REGISTRY).map(([key, value]) => [key, value.label]),
) as Record<SupportedModule, string>

export function getModuleLabel(module: SupportedModule): string {
  return MODULE_REGISTRY[module].label
}

export function getActionLabel(
  module: SupportedModule,
  action: string,
): string | undefined {
  const actions = MODULE_REGISTRY[module].actions
  return actions[action as keyof typeof actions]
}

export function checkIsModuleAction(
  module: SupportedModule,
  action: string,
): action is keyof (typeof MODULE_REGISTRY)[typeof module]['actions'] {
  const actions = MODULE_REGISTRY[module].actions
  return Object.hasOwn(actions, action)
}

export function getActionDescription(
  module: SupportedModule,
  action: string,
): string | undefined {
  const descriptions = MODULE_REGISTRY[module].descriptions
  return descriptions[action as keyof typeof descriptions]
}

// ts-proto v2+ generates types using @bufbuild/protobuf's BinaryWriter,
// while CosmJS expects protobufjs's Writer. The types are incompatible at compile time,
// but functionally equivalent at runtime (both have encode/decode/fromPartial methods).
// We use 'as unknown as GeneratedType' to bridge this type incompatibility.
function mapToGeneratedTypes(
  entries: readonly [string, unknown][],
): [string, GeneratedType][] {
  return entries.map(([typeUrl, msg]): [string, GeneratedType] => [
    typeUrl,
    msg as unknown as GeneratedType,
  ])
}

export const moduleRegistry = new Registry([
  ...defaultRegistryTypes,
  ...mapToGeneratedTypes([
    ['/core.bridge.MsgInsertChain', MsgInsertChain],
    ['/core.bridge.MsgDeleteChain', MsgDeleteChain],
    ['/core.bridge.MsgInsertToken', MsgInsertToken],
    ['/core.bridge.MsgUpdateToken', MsgUpdateToken],
    ['/core.bridge.MsgDeleteToken', MsgDeleteToken],
    ['/core.bridge.MsgAddTokenInfo', MsgAddTokenInfo],
    ['/core.bridge.MsgRemoveTokenInfo', MsgRemoveTokenInfo],
    ['/core.bridge.MsgStartEpoch', MsgStartEpoch],
    ['/core.bridge.MsgRemoveTransaction', MsgRemoveTransaction],
    ['/core.bridge.MsgAddTxToStopList', MsgAddTxToStopList],
    ['/core.bridge.MsgRemoveTxFromStopList', MsgRemoveTxFromStopList],
    ['/core.bridge.MsgSetReferral', MsgSetReferral],
    ['/core.bridge.MsgRemoveReferral', MsgRemoveReferral],
  ]),
])
